import React, { useState, useEffect } from 'react';
import { GraduationCap, CheckCircle, AlertCircle, Loader2, ChevronDown, ArrowLeft } from 'lucide-react';
import api from '../api';

const CLASSES = [
  'Nursery 1', 'Nursery 2', 'Primary 1', 'Primary 2', 'Primary 3', 'Primary 4', 'Primary 5', 'Primary 6',
  'JSS 1', 'JSS 2', 'JSS 3', 'SSS 1', 'SSS 2', 'SSS 3'
];

const INIT = {
  school_id: '', first_name: '', last_name: '', date_of_birth: '', gender: '',
  applying_for_class: '', previous_school: '',
  guardian_name: '', guardian_relationship: 'Parent', guardian_phone: '', guardian_email: '', address: '', notes: '',
};

export default function ApplyPage() {
  const [schools, setSchools] = useState([]);
  const [form, setForm] = useState(INIT);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [loadingSchools, setLoadingSchools] = useState(true);

  useEffect(() => {
    api.get('/admissions/schools')
      .then(res => {
        const list = res.data || [];
        setSchools(list);
        if (list.length === 1) setForm(f => ({ ...f, school_id: list[0].id }));
      })
      .catch(() => {})
      .finally(() => setLoadingSchools(false));
  }, []);

  const validate = () => {
    const e = {};
    if (!form.school_id) e.school_id = 'Please select a school';
    if (!form.first_name.trim()) e.first_name = 'Required';
    if (!form.last_name.trim()) e.last_name = 'Required';
    if (!form.date_of_birth) e.date_of_birth = 'Required';
    if (!form.gender) e.gender = 'Required';
    if (!form.applying_for_class) e.applying_for_class = 'Required';
    if (!form.guardian_name.trim()) e.guardian_name = 'Required';
    if (!form.guardian_phone.trim()) e.guardian_phone = 'Required';
    if (!form.guardian_email.trim()) e.guardian_email = 'Required';
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    try {
      await api.post('/admissions/apply', form);
      setSuccess(true);
    } catch (err) {
      setErrors({ _global: err?.detail || 'Submission failed. Please try again.' });
    } finally { setSubmitting(false); }
  };

  const sel = (field, value) => { setForm(f => ({ ...f, [field]: value })); setErrors(e => { const n = { ...e }; delete n[field]; return n; }); };

  const school = schools.find(s => s.id === form.school_id);

  if (success) return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-9 h-9 text-green-600" />
        </div>
        <h2 className="text-2xl font-extrabold text-slate-900 mb-2">Application Submitted!</h2>
        <p className="text-slate-600 mb-4">
          Thank you for applying to <strong>{school?.name}</strong>. Your application has been received and is under review.
        </p>
        <p className="text-sm text-slate-500 mb-6">
          The school will contact you at <strong>{form.guardian_email}</strong> regarding your application status.
        </p>
        <button onClick={() => { setForm(INIT); setSuccess(false); setErrors({}); }}
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-xl transition-colors">
          Submit Another Application
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-slate-100">
      {/* Header */}
      <header className="bg-[#0F1623] shadow-lg border-b border-slate-800">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#516EFA] rounded-xl flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-extrabold text-white tracking-tight">EDUVA</span>
              <p className="text-xs text-slate-400 leading-none">School Admission Portal</p>
            </div>
          </div>
          <a href="/" className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to home
          </a>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Page title */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-extrabold text-slate-900 mb-2">Online Admission Application</h1>
          <p className="text-slate-500">Fill in the details below to apply for admission. All fields marked * are required.</p>
        </div>

        {errors._global && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{errors._global}</p>
          </div>
        )}

        <form onSubmit={submit} data-testid="apply-form" className="space-y-6">
          {/* School Selection */}
          <Section title="Select School">
            {loadingSchools ? (
              <div className="flex items-center gap-2 py-4 text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading schools...
              </div>
            ) : schools.length === 1 ? (
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex items-center gap-3">
                {school?.logo
                  ? <img src={school.logo} alt="logo" className="w-10 h-10 rounded-full object-cover" />
                  : <div className="w-10 h-10 bg-indigo-200 rounded-full flex items-center justify-center">
                      <GraduationCap className="w-5 h-5 text-indigo-700" />
                    </div>}
                <div>
                  <p className="font-bold text-indigo-800">{school?.name}</p>
                  <p className="text-xs text-indigo-600">{school?.address}</p>
                </div>
              </div>
            ) : (
              <div>
                <Label text="School *" error={errors.school_id} />
                <div className="relative">
                  <select value={form.school_id} onChange={e => sel('school_id', e.target.value)}
                    className={`w-full border rounded-xl px-4 py-3 text-sm bg-white outline-none focus:border-indigo-400 appearance-none pr-10 ${errors.school_id ? 'border-red-300' : 'border-slate-200'}`}
                    data-testid="apply-school">
                    <option value="">Select a school...</option>
                    {schools.map(s => <option key={s.id} value={s.id}>{s.name} — {s.city}</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
                {errors.school_id && <ErrMsg msg={errors.school_id} />}
              </div>
            )}
          </Section>

          {/* Student Information */}
          <Section title="Student Information">
            <div className="grid grid-cols-2 gap-4">
              <Field label="First Name *" error={errors.first_name}>
                <input type="text" value={form.first_name} onChange={e => sel('first_name', e.target.value)}
                  className={Input(errors.first_name)} placeholder="First name" data-testid="apply-first-name" />
              </Field>
              <Field label="Last Name *" error={errors.last_name}>
                <input type="text" value={form.last_name} onChange={e => sel('last_name', e.target.value)}
                  className={Input(errors.last_name)} placeholder="Last name" data-testid="apply-last-name" />
              </Field>
              <Field label="Date of Birth *" error={errors.date_of_birth}>
                <input type="date" value={form.date_of_birth} onChange={e => sel('date_of_birth', e.target.value)}
                  className={Input(errors.date_of_birth)} data-testid="apply-dob" />
              </Field>
              <Field label="Gender *" error={errors.gender}>
                <div className="relative">
                  <select value={form.gender} onChange={e => sel('gender', e.target.value)}
                    className={`${Input(errors.gender)} appearance-none pr-10`} data-testid="apply-gender">
                    <option value="">Select...</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              </Field>
              <Field label="Class Applying For *" error={errors.applying_for_class}>
                <div className="relative">
                  <select value={form.applying_for_class} onChange={e => sel('applying_for_class', e.target.value)}
                    className={`${Input(errors.applying_for_class)} appearance-none pr-10`} data-testid="apply-class">
                    <option value="">Select class...</option>
                    {CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              </Field>
              <Field label="Previous School">
                <input type="text" value={form.previous_school} onChange={e => sel('previous_school', e.target.value)}
                  className={Input()} placeholder="Previous school (if any)" />
              </Field>
            </div>
          </Section>

          {/* Guardian Information */}
          <Section title="Parent / Guardian Information">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Guardian Full Name *" error={errors.guardian_name}>
                <input type="text" value={form.guardian_name} onChange={e => sel('guardian_name', e.target.value)}
                  className={Input(errors.guardian_name)} placeholder="Full name" data-testid="apply-guardian-name" />
              </Field>
              <Field label="Relationship">
                <div className="relative">
                  <select value={form.guardian_relationship} onChange={e => sel('guardian_relationship', e.target.value)}
                    className={`${Input()} appearance-none pr-10`}>
                    <option>Parent</option>
                    <option>Father</option>
                    <option>Mother</option>
                    <option>Guardian</option>
                    <option>Sponsor</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              </Field>
              <Field label="Phone Number *" error={errors.guardian_phone}>
                <input type="tel" value={form.guardian_phone} onChange={e => sel('guardian_phone', e.target.value)}
                  className={Input(errors.guardian_phone)} placeholder="e.g. 08012345678" data-testid="apply-guardian-phone" />
              </Field>
              <Field label="Email Address *" error={errors.guardian_email}>
                <input type="email" value={form.guardian_email} onChange={e => sel('guardian_email', e.target.value)}
                  className={Input(errors.guardian_email)} placeholder="email@example.com" data-testid="apply-guardian-email" />
              </Field>
              <div className="col-span-2">
                <Field label="Home Address">
                  <input type="text" value={form.address} onChange={e => sel('address', e.target.value)}
                    className={Input()} placeholder="Full home address" />
                </Field>
              </div>
            </div>
          </Section>

          {/* Additional Notes */}
          <Section title="Additional Information (Optional)">
            <textarea value={form.notes} onChange={e => sel('notes', e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-indigo-400 resize-none h-24"
              placeholder="Any additional information you'd like to share with the school..." />
          </Section>

          <button type="submit" disabled={submitting}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 transition-colors text-base disabled:opacity-60"
            data-testid="submit-apply-btn">
            {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Submitting...</> : 'Submit Application'}
          </button>

          <p className="text-center text-xs text-slate-400">
            By submitting this form, you agree to provide accurate information for admission consideration.
          </p>
        </form>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="bg-slate-50 border-b border-slate-200 px-6 py-3">
        <h3 className="font-bold text-slate-700 text-sm uppercase tracking-wide">{title}</h3>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <Label text={label} error={error} />
      {children}
      {error && <ErrMsg msg={error} />}
    </div>
  );
}

function Label({ text, error }) {
  return <label className={`block text-xs font-semibold mb-1.5 ${error ? 'text-red-600' : 'text-slate-600'}`}>{text}</label>;
}

function ErrMsg({ msg }) {
  return <p className="text-xs text-red-500 mt-1">{msg}</p>;
}

function Input(error) {
  return `w-full border rounded-xl px-4 py-3 text-sm bg-white outline-none focus:border-indigo-400 transition-colors ${error ? 'border-red-300 focus:border-red-400' : 'border-slate-200'}`;
}
